import { GoogleTagManager, sendGTMEvent } from "@next/third-parties/google";
import * as stylex from "@stylexjs/stylex";
import useLocale from "hooks/useLocale";
import { Noto_Naskh_Arabic, Noto_Sans, Noto_Sans_SC } from "next/font/google";
import Head from "next/head";
import Router from "next/router";
import Script from "next/script";
import * as React from "react";
import { Favicon } from "../components/favicon";
import { HideGlobalSpinner } from "../components/spinner";
import { ConfigProvider } from "../config";
import { AppStateProvider } from "../domain/state";
import { useActiveAlias } from "../hooks";
import { LocaleProvider } from "../locales";
import { documentThemes } from "../materials/theme.stylex";
import { O, pipe } from "../prelude";
import { findArticleAlias } from "../routes";
import "../style.global.css";
const notoSans = Noto_Sans({
    weight: [
        "400",
        "700"
    ],
    subsets: [
        "latin"
    ],
    display: "swap",
    preload: true,
    variable: "--font-noto-sans"
});
const notoSansSC = Noto_Sans_SC({
    weight: [
        "400",
        "700"
    ],
    display: "swap",
    preload: false,
    variable: "--font-noto-sans-sc"
});
const notoNaskhArabic = Noto_Naskh_Arabic({
    weight: [
        "400",
        "700"
    ],
    subsets: [
        "arabic"
    ],
    display: "swap",
    preload: false,
    variable: "--font-noto-naskh-arabic"
});
// -----------------------------------------------------------------------------
// App
export default function App(props) {
    const locale = useLocale();
    // Google Tag Manager
    React.useEffect(()=>{
        if (!process.env.GTM_TRACKING_ID) return;
        const handleRouteChange = (url)=>sendGTMEvent({
                event: "pageview",
                page: url
            });
        Router.events.on("routeChangeComplete", handleRouteChange);
        return ()=>{
            Router.events.off("routeChangeComplete", handleRouteChange);
        };
    }, []);
    return <div className={`${notoSans.variable} ${notoSansSC.variable} ${notoNaskhArabic.variable}`}>
      <LocaleProvider locale={locale}>
        <ConfigProvider locale={locale}>
          {process.env.GTM_TRACKING_ID && <GoogleTagManager gtmId={process.env.GTM_TRACKING_ID}/>}
          <Script defer src="https://umami.interactivethings.io/script.js" data-website-id={process.env.NEXT_PUBLIC_UMAMI_WEBSITE_ID}/>
          <Head>
            <Favicon/>
          </Head>

          <HideGlobalSpinner/>
          <Main {...props}/>
        </ConfigProvider>
      </LocaleProvider>
    </div>;
}
// -----------------------------------------------------------------------------
// Main – this is the main app component
// WARNING: Re-renders the whole app on window resize
const Main = ({ Component, pageProps })=>{
    const locale = useLocale();
    const alias = useActiveAlias();
    const themeKey = pipe(findArticleAlias(alias), O.getOrElse(()=>"default"));
    return React.useMemo(()=>{
        return <div {...stylex.props(documentThemes[themeKey])}>
        <AppStateProvider>
          <Component {...pageProps} locale={locale} alias={alias}/>
        </AppStateProvider>
      </div>;
    }, [
        themeKey,
        locale,
        alias,
        Component,
        pageProps
    ]);
};
